import type {
  Artwork,
  AuditEntry,
  Category,
  ContactMessage,
  Locale,
  Media,
  Paginated,
  Role,
  Session,
  SitePage,
  SitemapData,
  Translation,
  User,
} from './types'

/**
 * The API base URL, fixed at build time.
 *
 * The same value is used by the browser and by the prerender step: Vite
 * substitutes it into both bundles, so there is one setting to get right
 * rather than two that can disagree.
 */
export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080'

/** A failed request, carrying the API's structured error body. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, string>

  constructor(status: number, message: string, code = '', details: Record<string, string> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** True when the session has expired and the user must sign in again. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Sends the session cookie. Required for everything under /admin. */
  auth?: boolean
  signal?: AbortSignal
  /** Set for multipart uploads, where the browser must pick the boundary. */
  raw?: BodyInit
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = false, signal, raw } = options

  const headers: Record<string, string> = {}
  let payload: BodyInit | undefined = raw

  if (body !== undefined && raw === undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: payload,
    // Cookies are cross-origin here (site and API are different hosts), so
    // they only travel when this is set explicitly.
    credentials: auth ? 'include' : 'omit',
    signal,
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const parsed: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    const errorBody = parsed as {
      error?: string
      code?: string
      details?: Record<string, string>
    } | null
    throw new ApiError(
      response.status,
      errorBody?.error ?? `request failed with status ${response.status}`,
      errorBody?.code,
      errorBody?.details
    )
  }

  return parsed as T
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

/* -------------------------------------------------------------------------
   Public endpoints
   ------------------------------------------------------------------------- */

export const publicApi = {
  categories: (locale: Locale, signal?: AbortSignal) =>
    request<{ items: Category[] }>(`/api/v1/categories${query({ locale })}`, { signal }),

  artworks: (
    params: { locale: Locale; category?: string; limit?: number; offset?: number },
    signal?: AbortSignal
  ) => request<Paginated<Artwork>>(`/api/v1/artworks${query(params)}`, { signal }),

  /** `ref` is "42-kit-bandyt"; only the leading number is significant. */
  artwork: (ref: string, locale: Locale, signal?: AbortSignal) =>
    request<Artwork>(`/api/v1/artworks/${encodeURIComponent(ref)}${query({ locale })}`, {
      signal,
    }),

  page: (slug: string, locale: Locale, signal?: AbortSignal) =>
    request<SitePage>(`/api/v1/pages/${encodeURIComponent(slug)}${query({ locale })}`, { signal }),

  sitemap: (signal?: AbortSignal) => request<SitemapData>('/api/v1/sitemap', { signal }),

  contact: (body: {
    name: string
    email: string
    message: string
    locale: Locale
    turnstileToken?: string
    website?: string
  }) => request<{ status: string }>('/api/v1/contact', { method: 'POST', body }),
}

/* -------------------------------------------------------------------------
   Authentication
   ------------------------------------------------------------------------- */

export const authApi = {
  login: (email: string, password: string) =>
    request<Session>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: true,
    }),

  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST', auth: true }),

  me: (signal?: AbortSignal) => request<Session>('/api/v1/auth/me', { auth: true, signal }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/v1/auth/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      auth: true,
    }),
}

/* -------------------------------------------------------------------------
   Admin endpoints
   ------------------------------------------------------------------------- */

export interface CategoryInput {
  slug?: string
  isPublished?: boolean
  iconMediaId?: string | null
  coverMediaId?: string | null
  clearIcon?: boolean
  clearCover?: boolean
  translations: Translation[]
}

export interface ArtworkInput {
  categoryId?: string
  slug?: string
  isPublished?: boolean
  createdOn?: string | null
  mediaIds?: string[]
  coverMediaId?: string | null
  translations: Translation[]
}

export const adminApi = {
  categories: (signal?: AbortSignal) =>
    request<{ items: Category[] }>('/api/v1/admin/categories', { auth: true, signal }),

  createCategory: (body: CategoryInput) =>
    request<Category>('/api/v1/admin/categories', { method: 'POST', body, auth: true }),

  updateCategory: (id: string, body: CategoryInput) =>
    request<Category>(`/api/v1/admin/categories/${id}`, { method: 'PATCH', body, auth: true }),

  deleteCategory: (id: string) =>
    request<void>(`/api/v1/admin/categories/${id}`, { method: 'DELETE', auth: true }),

  reorderCategories: (ids: string[]) =>
    request<void>('/api/v1/admin/categories/reorder', {
      method: 'POST',
      body: { ids },
      auth: true,
    }),

  artworks: (
    params: { category?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal
  ) =>
    request<Paginated<Artwork>>(`/api/v1/admin/artworks${query(params)}`, { auth: true, signal }),

  artwork: (id: string, signal?: AbortSignal) =>
    request<Artwork>(`/api/v1/admin/artworks/${id}`, { auth: true, signal }),

  createArtwork: (body: ArtworkInput) =>
    request<Artwork>('/api/v1/admin/artworks', { method: 'POST', body, auth: true }),

  updateArtwork: (id: string, body: ArtworkInput) =>
    request<Artwork>(`/api/v1/admin/artworks/${id}`, { method: 'PATCH', body, auth: true }),

  deleteArtwork: (id: string) =>
    request<void>(`/api/v1/admin/artworks/${id}`, { method: 'DELETE', auth: true }),

  reorderArtworks: (ids: string[]) =>
    request<void>('/api/v1/admin/artworks/reorder', { method: 'POST', body: { ids }, auth: true }),

  /** Uploads photographs. The browser sets the multipart boundary itself. */
  uploadMedia: (files: File[], alt?: { altUk?: string; altEn?: string }) => {
    const form = new FormData()
    for (const file of files) form.append('files', file)
    if (alt?.altUk) form.append('altUk', alt.altUk)
    if (alt?.altEn) form.append('altEn', alt.altEn)
    return request<{ items: Media[]; failed: Record<string, string> }>('/api/v1/admin/media', {
      method: 'POST',
      raw: form,
      auth: true,
    })
  },

  media: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    request<{ items: Media[]; limit: number; offset: number }>(
      `/api/v1/admin/media${query(params)}`,
      { auth: true, signal }
    ),

  orphanMedia: (signal?: AbortSignal) =>
    request<{ items: Media[] }>('/api/v1/admin/media/orphans', { auth: true, signal }),

  updateMediaAlt: (id: string, altUk: string, altEn: string) =>
    request<Media>(`/api/v1/admin/media/${id}`, {
      method: 'PATCH',
      body: { altUk, altEn },
      auth: true,
    }),

  deleteMedia: (id: string) =>
    request<void>(`/api/v1/admin/media/${id}`, { method: 'DELETE', auth: true }),

  page: (slug: string, signal?: AbortSignal) =>
    request<SitePage>(`/api/v1/admin/pages/${slug}`, { auth: true, signal }),

  savePage: (
    slug: string,
    body: {
      translations: SitePage['translations']
      coverMediaId?: string | null
      clearCover?: boolean
    }
  ) => request<SitePage>(`/api/v1/admin/pages/${slug}`, { method: 'PUT', body, auth: true }),

  users: (signal?: AbortSignal) =>
    request<{ items: User[]; roles: Role[] }>('/api/v1/admin/users', { auth: true, signal }),

  createUser: (body: { email: string; displayName: string; password: string; role: Role }) =>
    request<User>('/api/v1/admin/users', { method: 'POST', body, auth: true }),

  updateUser: (id: string, body: { displayName?: string; role?: Role; isActive?: boolean }) =>
    request<User>(`/api/v1/admin/users/${id}`, { method: 'PATCH', body, auth: true }),

  resetUserPassword: (id: string, password: string) =>
    request<void>(`/api/v1/admin/users/${id}/password`, {
      method: 'POST',
      body: { password },
      auth: true,
    }),

  deleteUser: (id: string) =>
    request<void>(`/api/v1/admin/users/${id}`, { method: 'DELETE', auth: true }),

  messages: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    request<Paginated<ContactMessage> & { unread: number }>(
      `/api/v1/admin/messages${query(params)}`,
      { auth: true, signal }
    ),

  markMessage: (id: string, isRead: boolean) =>
    request<void>(`/api/v1/admin/messages/${id}`, {
      method: 'PATCH',
      body: { isRead },
      auth: true,
    }),

  deleteMessage: (id: string) =>
    request<void>(`/api/v1/admin/messages/${id}`, { method: 'DELETE', auth: true }),

  audit: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
    request<{ items: AuditEntry[] }>(`/api/v1/admin/audit${query(params)}`, { auth: true, signal }),

  rebuild: () =>
    request<{ status: string }>('/api/v1/admin/rebuild', { method: 'POST', auth: true }),
}
